import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  TableSortLabel,
  TablePagination
} from '@mui/material';
import {
  Add,
  Delete,
  Visibility,
  Edit as EditIcon,
  Create as CreateWorkOrderIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  FileDownload as FileDownloadIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import axios from 'axios';
import { useADSBServiceBulletins } from './adsb/hooks/useADSBServiceBulletins';
import CreateEditADSBulletinDialog from './adsb/CreateEditADSBulletinDialog';
import CreateIntimationFromADSBulletinDialog from './adsb/CreateIntimationFromADSBulletinDialog';
import { ROLES } from '../constants/roles';
import { roleMatches } from '../utils/rolePermissions';
import {
  CLASSIFICATION_OPTIONS,
  PRIORITY_OPTIONS,
  REGULATORY_AUTHORITY_OPTIONS,
  SOURCE_OPTIONS,
  priorityLabel
} from './adsb/constants/adsbConstants';
import { BULLETIN_FILTER_LABELS } from './maintenance/utils/maintenanceUtils';

const STATUS_FILTER_OPTIONS = [
  { value: 'ALL', label: 'All statuses' },
  { value: 'OPEN', label: 'Open' },
  { value: 'CLOSED', label: 'Closed' }
];

const ADSB_ROLES = [ROLES.SUPER_ADMIN, ROLES.ACCOUNTABLE_MANAGER, ROLES.CA_MANAGER, ROLES.CA_TECHNICAL_OFFICER, ROLES.CAMO_QM_MANAGER, ROLES.CAMO_QM_OFFICER, ROLES.PLANNING_MANAGER];

/** Flatten API scheduleRemaining for table cell + CSV (live TLB vs last performed). */
const formatBulletinRemainingLines = (b) => {
  const sr = b.scheduleRemaining;
  if (!sr || !sr.length) return [];
  const lines = [];
  for (const block of sr) {
    if (!block.basedOn || !block.rows?.length) continue;
    for (const r of block.rows) {
      const detail = r.secondaryRemaining ? `${r.primaryRemaining} · ${r.secondaryRemaining}` : r.primaryRemaining;
      lines.push(`${r.registration}: ${detail}`);
    }
  }
  return lines;
};

const formatBulletinLastPerformedLines = (b) => {
  const sr = b.scheduleRemaining;
  if (!sr || !sr.length) return [];
  const lines = [];
  for (const block of sr) {
    if (!block.rows?.length) continue;
    for (const r of block.rows) {
      lines.push(`${r.registration}: ${r.lastPerformedAtDisplay || '—'}`);
    }
  }
  return lines;
};

const formatBulletinCurrentValueLines = (b) => {
  const sr = b.scheduleRemaining;
  if (!sr || !sr.length) return [];
  const lines = [];
  for (const block of sr) {
    if (!block.rows?.length) continue;
    for (const r of block.rows) {
      lines.push(`${r.registration}: ${r.currentValueDisplay || '—'}`);
    }
  }
  return lines;
};

/** Next Due At lines from API (hours/cycles/calendar); same shape as AMP schedules. */
const formatBulletinNextDueAtForCsv = (b) => {
  const entries = b.nextDueAtEntries;
  if (!entries || !entries.length) return '';
  return entries.map((e) => e.text).join('; ');
};

const formatHoursValue = (value) => {
  const n = Number(value);
  if (Number.isNaN(n)) return null;
  const h = Math.floor(n);
  let m = Math.round((n % 1) * 60);
  if (m >= 60) m = 59;
  if (m < 0) m = 0;
  return `${h}h ${String(m).padStart(2, '0')}m`;
};

const toIsoDate = (date) => {
  if (!date) return null;
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString().slice(0, 10);
};

const addCalendarInterval = (baseDate, basedOn, interval) => {
  const d = new Date(baseDate);
  if (basedOn === 'DAYS') d.setDate(d.getDate() + Math.floor(interval));
  else if (basedOn === 'MONTHS') d.setMonth(d.getMonth() + Math.floor(interval));
  else if (basedOn === 'YEARS') d.setFullYear(d.getFullYear() + Math.floor(interval));
  return d;
};

/** Build per-aircraft Next Due as last performed + recurrence value. */
const getAircraftNextDueLines = (bulletin, selectedAircraftId = 'ALL') => {
  // Completed one-time: stop due calculation (show Completed via API nextDueAtEntries).
  if (bulletin?.recurrenceType === 'ONE_TIME' && bulletin?.oneTimeStatus === 'COMPLETED') {
    return [];
  }

  const aircraftAssemblies = Array.isArray(bulletin?.aircraftAssemblies) ? bulletin.aircraftAssemblies : [];
  const scheduleEntries = Array.isArray(bulletin?.schedules) && bulletin.schedules.length > 0
    ? bulletin.schedules.filter((s) => s?.recurrenceBasedOn && s?.recurrenceValue != null)
    : bulletin?.recurrenceBasedOn && bulletin?.recurrenceValue != null
      ? [{
          recurrenceBasedOn: bulletin.recurrenceBasedOn,
          recurrenceValue: bulletin.recurrenceValue,
          recurrenceValue2: bulletin.recurrenceValue2,
          matchType: bulletin.matchType || 'EXACTLY',
          lastDoneValue: null,
          lastDoneByAircraft: []
        }]
      : [];

  if (!scheduleEntries.length) return [];

  const lines = [];
  for (const aa of aircraftAssemblies) {
    const aircraft = aa?.aircraftId;
    const aircraftId = aircraft?._id != null ? String(aircraft._id) : aircraft != null ? String(aircraft) : null;
    if (!aircraftId) continue;
    if (selectedAircraftId !== 'ALL' && String(selectedAircraftId) !== aircraftId) continue;
    const registration = aircraft?.registration || '—';

    const dueParts = [];
    for (const entry of scheduleEntries) {
      const basedOn = entry.recurrenceBasedOn;
      const matchType = entry.matchType || 'EXACTLY';
      const val1 = Number(entry.recurrenceValue);
      const val2 = entry.recurrenceValue2 != null ? Number(entry.recurrenceValue2) : null;
      if (Number.isNaN(val1)) continue;

      const aircraftLastDone = Array.isArray(entry.lastDoneByAircraft)
        ? entry.lastDoneByAircraft.find((x) => String(x?.aircraftId) === aircraftId)
        : null;
      const lastDoneValue = aircraftLastDone?.lastDoneValue != null
        ? aircraftLastDone.lastDoneValue
        : entry.lastDoneValue;

      if (['HOURS', 'CYCLES', 'LANDINGS'].includes(basedOn)) {
        const lastDone = lastDoneValue != null && lastDoneValue !== '' ? Number(lastDoneValue) : 0;
        if (Number.isNaN(lastDone)) continue;
        const unit = basedOn === 'HOURS' ? '' : ` ${basedOn === 'CYCLES' ? 'Cycles' : 'Landings'}`;
        if (matchType === 'BETWEEN' && val2 != null && !Number.isNaN(val2)) {
          if (basedOn === 'HOURS') {
            const low = formatHoursValue(lastDone + val1);
            const high = formatHoursValue(lastDone + val2);
            if (low && high) dueParts.push(`Between ${low} - ${high}`);
          } else {
            dueParts.push(`Between ${lastDone + val1} - ${lastDone + val2}${unit}`);
          }
        } else if (basedOn === 'HOURS') {
          const hoursText = formatHoursValue(lastDone + val1);
          if (hoursText) dueParts.push(hoursText);
        } else {
          dueParts.push(`${lastDone + val1}${unit}`);
        }
        continue;
      }

      if (['DAYS', 'MONTHS', 'YEARS'].includes(basedOn)) {
        const baseDate = lastDoneValue != null && lastDoneValue !== ''
          ? new Date(lastDoneValue)
          : bulletin?.createdAt
            ? new Date(bulletin.createdAt)
            : null;
        if (!baseDate || Number.isNaN(baseDate.getTime())) continue;
        if (matchType === 'BETWEEN' && val2 != null && !Number.isNaN(val2)) {
          const low = toIsoDate(addCalendarInterval(baseDate, basedOn, val1));
          const high = toIsoDate(addCalendarInterval(baseDate, basedOn, val2));
          if (low && high) dueParts.push(`Between ${low} - ${high}`);
        } else {
          const iso = toIsoDate(addCalendarInterval(baseDate, basedOn, val1));
          if (iso) dueParts.push(iso);
        }
      }
    }

    lines.push({
      aircraftId,
      registration,
      dueText: dueParts.length ? Array.from(new Set(dueParts)).join(' | ') : '—'
    });
  }

  lines.sort((a, b) => String(a.registration).localeCompare(String(b.registration)));
  return lines;
};

const classificationLabel = (v) => CLASSIFICATION_OPTIONS.find(o => o.value === v)?.label ?? v ?? '—';
const sourceLabel = (v) => SOURCE_OPTIONS.find(o => o.value === v)?.label ?? v ?? '—';

const formatAssemblyLabel = (asm) => {
  if (!asm) return '';
  if (typeof asm !== 'object') return String(asm);
  const typeName = asm.typeName || asm.type?.name || 'Assembly';
  const sn = asm.serialNumber || 'N/A';
  return `${typeName} – SN: ${sn}`;
};

const aircraftSummary = (b) => {
  const aa = b.aircraftAssemblies || [];
  if (aa.length === 0) return '—';
  return aa.map(a => a.aircraftId?.registration || a.aircraftId).filter(Boolean).join(', ');
};

const assemblySummary = (b) => {
  const aa = b.aircraftAssemblies || [];
  const labels = [];
  for (const entry of aa) {
    for (const asm of (entry.assemblyIds || [])) {
      if (!asm) continue;
      const label = formatAssemblyLabel(asm);
      if (label) labels.push(label);
    }
  }
  return labels.length ? labels.join(', ') : '—';
};

const BulletinActionButtons = React.memo(({ bulletin, onView, onEdit, onCreateIntimation, onDelete }) => {
  const handleView = useCallback(() => { onView(bulletin); }, [bulletin, onView]);
  const handleEdit = useCallback(() => { onEdit(bulletin); }, [bulletin, onEdit]);
  const handleCreateIntimation = useCallback(() => { onCreateIntimation(bulletin); }, [bulletin, onCreateIntimation]);
  const handleDelete = useCallback(() => { onDelete(bulletin); }, [bulletin, onDelete]);
  return (
    <Box sx={{ display: 'inline-flex', gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
      <Tooltip title="View">
        <IconButton size="small" onClick={handleView}>
          <Visibility fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Edit">
        <IconButton size="small" onClick={handleEdit}>
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Create WO Intimation">
        <IconButton size="small" color="primary" onClick={handleCreateIntimation}>
          <CreateWorkOrderIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Delete">
        <IconButton size="small" color="error" onClick={handleDelete}>
          <Delete fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
});
BulletinActionButtons.displayName = 'BulletinActionButtons';

const ADSBServiceBulletins = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [classificationFilter, setClassificationFilter] = useState('ALL');
  const [priorityFilter, setPriorityFilter] = useState('ALL');
  const [sourceFilter, setSourceFilter] = useState('ALL');
  const [regulatoryAuthorityFilter, setRegulatoryAuthorityFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [bulletinTypeFilter, setBulletinTypeFilter] = useState('ALL');
  const [aircraftFilter, setAircraftFilter] = useState('ALL');
  const [assemblyFilter, setAssemblyFilter] = useState('ALL');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedBulletin, setSelectedBulletin] = useState(null);
  const [viewMode, setViewMode] = useState(true);
  const [createIntimationOpen, setCreateIntimationOpen] = useState(false);
  const [bulletinForIntimation, setBulletinForIntimation] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [bulletinToDelete, setBulletinToDelete] = useState(null);
  const [sortBy, setSortBy] = useState('title');
  const [sortOrder, setSortOrder] = useState('asc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const { user } = useAuth();
  const { showToast } = useToast();
  const { bulletins, aircraft, assemblies, loading, fetchBulletins, fetchAircraft, fetchAssemblies } = useADSBServiceBulletins();
  const canAccess = roleMatches(user?.role, ADSB_ROLES);
  const [searchParams, setSearchParams] = useSearchParams();
  const processedBulletinIdFromUrlRef = useRef(null);

  useEffect(() => {
    fetchAircraft();
  }, [fetchAircraft]);

  useEffect(() => {
    if (canAccess) {
      fetchAssemblies(aircraftFilter);
    }
  }, [canAccess, aircraftFilter, fetchAssemblies]);

  // Deep link: /adsb-service-bulletin?bulletinId=… (e.g. Maintenance → Upcoming → View)
   useEffect(() => {
    if (!canAccess || loading) return;
    const id = searchParams.get('bulletinId');
    if (!id) {
      processedBulletinIdFromUrlRef.current = null;
      return;
    }
    if (processedBulletinIdFromUrlRef.current === id) return;
    let cancelled = false;
    (async () => {
      try {
        let b = bulletins.find((x) => String(x._id) === String(id));
        if (!b) {
          const res = await axios.get(`/api/adsb-service-bulletins/${id}`);
          b = res.data;
        }
        if (cancelled || !b) return;
        processedBulletinIdFromUrlRef.current = id;
        setSelectedBulletin(b);
        setViewMode(true);
        setOpenDialog(true);
        const next = new URLSearchParams(searchParams);
        next.delete('bulletinId');
        setSearchParams(next, { replace: true });
      } catch (e) {
        if (!cancelled) {
          showToast(e.response?.data?.message || 'Could not open AD/SB bulletin from link', 'error');
          processedBulletinIdFromUrlRef.current = id;
          const next = new URLSearchParams(searchParams);
          next.delete('bulletinId');
          setSearchParams(next, { replace: true });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canAccess, loading, bulletins, searchParams, setSearchParams, showToast]);

  useEffect(() => {
    if (canAccess) {
      fetchBulletins({
        classification: classificationFilter,
        priority: priorityFilter,
        source: sourceFilter,
        regulatoryAuthority: regulatoryAuthorityFilter,
        statusFilter,
        bulletinTypeFilter,
        aircraftId: aircraftFilter,
        assemblyId: assemblyFilter
      });
    }
  }, [
    canAccess,
    classificationFilter,
    priorityFilter,
    sourceFilter,
    regulatoryAuthorityFilter,
    statusFilter,
    bulletinTypeFilter,
    aircraftFilter,
    assemblyFilter,
    fetchBulletins
  ]);

  const filteredBulletins = React.useMemo(() => {
    return bulletins.filter(b => {
      const matchSearch = !searchTerm ||
        (b.title && b.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (b.description && b.description.toLowerCase().includes(searchTerm.toLowerCase()));
      return matchSearch;
    });
  }, [bulletins, searchTerm]);

  const getAdsbBulletinNumber = useCallback((b) => {
    if (!b) return '—';
    if (b.adsbBulletinNumber) return b.adsbBulletinNumber;
    if (b._id) return 'ADSB-' + b._id.toString().slice(-8).toLowerCase();
    return '—';
  }, []);

  const handleSortRequest = useCallback((columnId) => {
    const isAsc = sortBy === columnId && sortOrder === 'asc';
    setSortBy(columnId);
    setSortOrder(isAsc ? 'desc' : 'asc');
    setPage(0);
  }, [sortBy, sortOrder]);

  const sortedBulletins = React.useMemo(() => {
    const list = [...filteredBulletins];
    const mult = sortOrder === 'asc' ? 1 : -1;
    const cmp = (a, b) => {
      let va, vb;
      switch (sortBy) {
        case 'adsbNumber':
          va = getAdsbBulletinNumber(a);
          vb = getAdsbBulletinNumber(b);
          return mult * (String(va || '').localeCompare(String(vb || '')));
        case 'title':
          va = (a.title || '').toLowerCase();
          vb = (b.title || '').toLowerCase();
          return mult * va.localeCompare(vb);
        case 'classification':
          va = classificationLabel(a.classification) || '';
          vb = classificationLabel(b.classification) || '';
          return mult * va.localeCompare(vb);
        case 'priority':
          va = priorityLabel(a.priority) || '';
          vb = priorityLabel(b.priority) || '';
          return mult * va.localeCompare(vb);
        case 'source':
          va = (sourceLabel(a.source) + (a.source === 'OTHER' && a.sourceCustom ? a.sourceCustom : '')).toLowerCase();
          vb = (sourceLabel(b.source) + (b.source === 'OTHER' && b.sourceCustom ? b.sourceCustom : '')).toLowerCase();
          return mult * va.localeCompare(vb);
        case 'aircraft':
          va = (a.aircraftAssemblies || []).map(aa => aa.aircraftId?.registration || '').join(', ').toLowerCase();
          vb = (b.aircraftAssemblies || []).map(aa => aa.aircraftId?.registration || '').join(', ').toLowerCase();
          return mult * va.localeCompare(vb);
        case 'assembly':
          va = assemblySummary(a).toLowerCase();
          vb = assemblySummary(b).toLowerCase();
          return mult * va.localeCompare(vb);
        case 'recurrence':
          va = a.recurrenceType === 'RECURRING'
            ? (a.matchType === 'BETWEEN' && a.recurrenceValue2 != null ? `Every ${a.recurrenceValue ?? ''}–${a.recurrenceValue2} ${a.recurrenceBasedOn || ''}` : `Every ${a.recurrenceValue ?? ''} ${a.recurrenceBasedOn || ''}`)
            : 'One-time';
          vb = b.recurrenceType === 'RECURRING'
            ? (b.matchType === 'BETWEEN' && b.recurrenceValue2 != null ? `Every ${b.recurrenceValue ?? ''}–${b.recurrenceValue2} ${b.recurrenceBasedOn || ''}` : `Every ${b.recurrenceValue ?? ''} ${b.recurrenceBasedOn || ''}`)
            : 'One-time';
          return mult * va.localeCompare(vb);
        case 'nextDueDate': {
          va = a.nextDueDate ? new Date(a.nextDueDate).getTime() : 0;
          vb = b.nextDueDate ? new Date(b.nextDueDate).getTime() : 0;
          return mult * (va - vb);
        }
        case 'checks': {
          va = a.adsbChecks?.length ?? 0;
          vb = b.adsbChecks?.length ?? 0;
          return mult * (va - vb);
        }
        case 'workOrders': {
          va = a.workOrders?.length ?? 0;
          vb = b.workOrders?.length ?? 0;
          return mult * (va - vb);
        }
        default:
          return 0;
      }
    };
    list.sort(cmp);
    return list;
  }, [filteredBulletins, sortBy, sortOrder, getAdsbBulletinNumber]);

  const paginatedBulletins = React.useMemo(() => {
    const start = page * rowsPerPage;
    return sortedBulletins.slice(start, start + rowsPerPage);
  }, [sortedBulletins, page, rowsPerPage]);

  useEffect(() => {
    setPage(0);
  }, [
    filteredBulletins.length,
    classificationFilter,
    priorityFilter,
    sourceFilter,
    regulatoryAuthorityFilter,
    statusFilter,
    bulletinTypeFilter,
    aircraftFilter,
    assemblyFilter,
    searchTerm
  ]);

  const handleCreate = useCallback(() => {
    setSelectedBulletin(null);
    setViewMode(false);
    setOpenDialog(true);
  }, []);

  const handleView = useCallback((bulletin) => {
    setSelectedBulletin(bulletin);
    setViewMode(true);
    setOpenDialog(true);
  }, []);

  const handleEdit = useCallback((bulletin) => {
    setSelectedBulletin(bulletin);
    setViewMode(false);
    setOpenDialog(true);
  }, []);

  const handleCreateIntimation = useCallback((bulletin) => {
    setBulletinForIntimation(bulletin);
    setCreateIntimationOpen(true);
  }, []);

  const handleRowClick = useCallback((bulletin) => {
    setSelectedBulletin(bulletin);
    setViewMode(true);
    setOpenDialog(true);
  }, []);

  const handleSwitchToEdit = useCallback(() => {
    setViewMode(false);
  }, []);

  const handleDeleteClick = useCallback((bulletin) => {
    setBulletinToDelete(bulletin);
    setDeleteConfirmOpen(true);
  }, []);

  const handleSearchChange = useCallback((e) => setSearchTerm(e.target.value), []);
  const handleClearSearch = useCallback(() => setSearchTerm(''), []);
  const handleClassificationChange = useCallback((e) => setClassificationFilter(e.target.value), []);
  const handlePriorityChange = useCallback((e) => setPriorityFilter(e.target.value), []);
  const handleSourceChange = useCallback((e) => setSourceFilter(e.target.value), []);
  const handleRegulatoryAuthorityChange = useCallback((e) => setRegulatoryAuthorityFilter(e.target.value), []);
  const handleStatusFilterChange = useCallback((e) => setStatusFilter(e.target.value), []);
  const handleBulletinTypeFilterChange = useCallback((e) => setBulletinTypeFilter(e.target.value), []);
  const handleAircraftChange = useCallback((e) => {
    setAircraftFilter(e.target.value);
    setAssemblyFilter('ALL');
  }, []);
  const handleAssemblyChange = useCallback((e) => setAssemblyFilter(e.target.value), []);

  const currentFilters = useCallback(() => ({
    classification: classificationFilter,
    priority: priorityFilter,
    source: sourceFilter,
    regulatoryAuthority: regulatoryAuthorityFilter,
    statusFilter,
    bulletinTypeFilter,
    aircraftId: aircraftFilter,
    assemblyId: assemblyFilter
  }), [
    classificationFilter,
    priorityFilter,
    sourceFilter,
    regulatoryAuthorityFilter,
    statusFilter,
    bulletinTypeFilter,
    aircraftFilter,
    assemblyFilter
  ]);

  const handleCloseDialog = useCallback(() => {
    setOpenDialog(false);
    setSelectedBulletin(null);
  }, []);
  const handleSuccess = useCallback(() => {
    fetchBulletins(currentFilters());
  }, [currentFilters, fetchBulletins]);
  const handleEvidenceUpload = useCallback((updated) => setSelectedBulletin(updated), []);
  const handleCloseCreateIntimation = useCallback(() => {
    setCreateIntimationOpen(false);
    setBulletinForIntimation(null);
  }, []);
  const handleSuccessCreateIntimation = useCallback(() => {
    fetchBulletins(currentFilters());
    setCreateIntimationOpen(false);
    setBulletinForIntimation(null);
  }, [currentFilters, fetchBulletins]);
  const handleCloseDelete = useCallback(() => setDeleteConfirmOpen(false), []);

  const handleDeleteConfirm = async () => {
    if (!bulletinToDelete) return;
    try {
      await axios.delete(`/api/adsb-service-bulletins/${bulletinToDelete._id}`);
      showToast('AD/SB bulletin deleted', 'success');
      fetchBulletins(currentFilters());
      setDeleteConfirmOpen(false);
      setBulletinToDelete(null);
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to delete bulletin', 'error');
    }
  };

  const escapeCsv = useCallback((value) => {
    if (value == null || value === '') return '';
    const str = String(value);
    if (str.includes('"') || str.includes(',') || str.includes('\n') || str.includes('\r')) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  }, []);

  const recurrenceDisplay = (b) => {
    if (b.recurrenceType === 'RECURRING') {
      return b.matchType === 'BETWEEN' && b.recurrenceValue2 != null
        ? `Every ${b.recurrenceValue ?? ''}–${b.recurrenceValue2} ${b.recurrenceBasedOn || ''}`
        : `Every ${b.recurrenceValue ?? ''} ${b.recurrenceBasedOn || ''}`;
    }
    return 'One-time';
  };

  const nextDueDisplayLines = useCallback((bulletin) => {
    if (bulletin?.recurrenceType === 'ONE_TIME' && bulletin?.oneTimeStatus === 'COMPLETED') {
      return ['Completed'];
    }

    const lines = getAircraftNextDueLines(bulletin, aircraftFilter);
    if (lines.length > 0) {
      return lines.map((l) => `${l.registration}: ${l.dueText || '—'}`);
    }

    const fallback = [];
    if (bulletin?.nextDueDate) {
      fallback.push(new Date(bulletin.nextDueDate).toLocaleDateString());
    }
    for (const entry of (bulletin?.nextDueAtEntries || [])) {
      if (entry?.text) fallback.push(entry.text);
    }
    return fallback;
  }, [aircraftFilter]);

  const handleExportCsv = useCallback(() => {
    const headers = [
      'AD/SB #',
      'ID',
      'Title',
      'Description',
      'Classification',
      'Priority',
      'Source',
      'Source (custom)',
      'Regulatory Authority',
      'Serial Numbers / Make-Model',
      'Aircraft',
      'Assembly',
      'Recurrence',
      'Next Due Date',
      'Next Due At',
      'Last Performed',
      'Current Airframe Value',
      'Remaining (live)',
      'Bulletin Applicable',
      'Applicability Remark',
      'Number of Checks',
      'AD/SB Checks (Details)',
      'Number of Work Orders',
      'Schedules / Last Performed',
      'Created At',
      'Updated At'
    ];
    const rows = sortedBulletins.map((b) => {
      const adsbChecksDetail = (b.adsbChecks && b.adsbChecks.length > 0)
        ? b.adsbChecks
            .sort((x, y) => (x.sequenceNumber ?? 0) - (y.sequenceNumber ?? 0))
            .map((c) => `${c.sequenceNumber ?? ''}: ${(c.description || '').replace(/\r?\n/g, ' ')}`.trim())
            .join('; ')
        : '';
      const schedulesDetail = (b.schedules && b.schedules.length > 0)
        ? b.schedules.map((s) => {
            const base = `${s.recurrenceBasedOn || ''} ${s.matchType === 'BETWEEN' ? `${s.recurrenceValue}–${s.recurrenceValue2}` : s.recurrenceValue}`;
            const lastDone = s.lastDoneValue != null ? ` (last: ${typeof s.lastDoneValue === 'string' && s.lastDoneValue.length >= 10 ? s.lastDoneValue.slice(0, 10) : s.lastDoneValue})` : '';
            return base.trim() + lastDone;
          }).join('; ')
        : '';
      return [
        getAdsbBulletinNumber(b),
        b._id,
        b.title ?? '',
        (b.description ?? '').replace(/\r?\n/g, ' '),
        classificationLabel(b.classification) ?? '',
        priorityLabel(b.priority),
        sourceLabel(b.source) ?? '',
        b.sourceCustom ?? '',
        b.regulatoryAuthorityName ?? '',
        (b.serialNumbersMakeModel ?? '').replace(/\r?\n/g, ' '),
        aircraftSummary(b),
        assemblySummary(b),
        recurrenceDisplay(b),
        b.nextDueDate ? new Date(b.nextDueDate).toISOString().slice(0, 10) : '',
        formatBulletinNextDueAtForCsv(b),
        formatBulletinLastPerformedLines(b).join('; '),
        formatBulletinCurrentValueLines(b).join('; '),
        formatBulletinRemainingLines(b).join('; '),
        b.bulletinApplicable !== false ? 'Yes' : 'No',
        (b.bulletinApplicabilityRemark ?? '').replace(/\r?\n/g, ' '),
        b.adsbChecks?.length ?? 0,
        adsbChecksDetail,
        b.workOrders?.length ?? 0,
        schedulesDetail,
        b.createdAt ? new Date(b.createdAt).toISOString() : '',
        b.updatedAt ? new Date(b.updatedAt).toISOString() : ''
      ].map(escapeCsv);
    });
    const csvContent = [headers.map(escapeCsv).join(','), ...rows.map((row) => row.join(','))].join('\r\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `adsb-service-bulletins-${new Date().toISOString().slice(0, 10)}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    showToast('AD/SB bulletin data exported to CSV', 'success');
  }, [sortedBulletins, getAdsbBulletinNumber, escapeCsv, showToast]);

  const handleChangePage = useCallback((_, newPage) => setPage(newPage), []);
  const handleChangeRowsPerPage = useCallback((e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  }, []);

  if (!canAccess) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">You do not have access to AD/SB Service Bulletins. Required roles: CA Manager, CA Technical Officer, Accountable Manager, Super Admin.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">AD/SB Service Bulletin</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<FileDownloadIcon />}
            onClick={handleExportCsv}
            disabled={loading || sortedBulletins.length === 0}
          >
            Export to CSV
          </Button>
          <Button variant="contained" startIcon={<Add />} onClick={handleCreate}>
            Create AD/SB Bulletin
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search by title or description..."
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchTerm ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={handleClearSearch}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null
            }}
            sx={{ minWidth: 220 }}
          />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Classification</InputLabel>
            <Select
              value={classificationFilter}
              label="Classification"
              onChange={handleClassificationChange}
            >
              <MenuItem value="ALL">All</MenuItem>
              {CLASSIFICATION_OPTIONS.map(o => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Priority</InputLabel>
            <Select value={priorityFilter} label="Priority" onChange={handlePriorityChange}>
              <MenuItem value="ALL">All</MenuItem>
              {PRIORITY_OPTIONS.map(o => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Source</InputLabel>
            <Select value={sourceFilter} label="Source" onChange={handleSourceChange}>
              <MenuItem value="ALL">All</MenuItem>
              {SOURCE_OPTIONS.map(o => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Regulatory Authority</InputLabel>
            <Select
              value={regulatoryAuthorityFilter}
              label="Regulatory Authority"
              onChange={handleRegulatoryAuthorityChange}
            >
              <MenuItem value="ALL">All</MenuItem>
              {REGULATORY_AUTHORITY_OPTIONS.map(o => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Status</InputLabel>
            <Select value={statusFilter} label="Status" onChange={handleStatusFilterChange}>
              {STATUS_FILTER_OPTIONS.map(o => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 260 }}>
            <InputLabel>Bulletin filter</InputLabel>
            <Select
              value={bulletinTypeFilter}
              label="Bulletin filter"
              onChange={handleBulletinTypeFilterChange}
              renderValue={(value) => BULLETIN_FILTER_LABELS[value] || value}
            >
              {Object.entries(BULLETIN_FILTER_LABELS).map(([value, label]) => (
                <MenuItem key={value} value={value}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Aircraft</InputLabel>
            <Select value={aircraftFilter} label="Aircraft" onChange={handleAircraftChange}>
              <MenuItem value="ALL">All</MenuItem>
              {aircraft.map(ac => (
                <MenuItem key={ac._id} value={ac._id}>{ac.registration}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 260 }}>
            <InputLabel>Assembly</InputLabel>
            <Select value={assemblyFilter} label="Assembly" onChange={handleAssemblyChange}>
              <MenuItem value="ALL">All</MenuItem>
              {assemblies.map(asm => (
                <MenuItem key={asm._id} value={asm._id}>{formatAssemblyLabel(asm)}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {loading ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">Loading bulletins...</Typography>
        </Paper>
      ) : filteredBulletins.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            {bulletins.length === 0
              ? 'No AD/SB bulletins. Use "Create AD/SB Bulletin" to add one.'
              : 'No bulletins match the current filters.'}
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sortDirection={sortBy === 'adsbNumber' ? sortOrder : false}>
                  <TableSortLabel
                    active={sortBy === 'adsbNumber'}
                    direction={sortBy === 'adsbNumber' ? sortOrder : 'asc'}
                    onClick={() => handleSortRequest('adsbNumber')}
                  >
                    AD/SB #
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortBy === 'title' ? sortOrder : false}>
                  <TableSortLabel
                    active={sortBy === 'title'}
                    direction={sortBy === 'title' ? sortOrder : 'asc'}
                    onClick={() => handleSortRequest('title')}
                  >
                    Title
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortBy === 'classification' ? sortOrder : false}>
                  <TableSortLabel
                    active={sortBy === 'classification'}
                    direction={sortBy === 'classification' ? sortOrder : 'asc'}
                    onClick={() => handleSortRequest('classification')}
                  >
                    Classification
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortBy === 'priority' ? sortOrder : false}>
                  <TableSortLabel
                    active={sortBy === 'priority'}
                    direction={sortBy === 'priority' ? sortOrder : 'asc'}
                    onClick={() => handleSortRequest('priority')}
                  >
                    Priority
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortBy === 'source' ? sortOrder : false}>
                  <TableSortLabel
                    active={sortBy === 'source'}
                    direction={sortBy === 'source' ? sortOrder : 'asc'}
                    onClick={() => handleSortRequest('source')}
                  >
                    Source
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortBy === 'aircraft' ? sortOrder : false}>
                  <TableSortLabel
                    active={sortBy === 'aircraft'}
                    direction={sortBy === 'aircraft' ? sortOrder : 'asc'}
                    onClick={() => handleSortRequest('aircraft')}
                  >
                    Aircraft
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortBy === 'assembly' ? sortOrder : false}>
                  <TableSortLabel
                    active={sortBy === 'assembly'}
                    direction={sortBy === 'assembly' ? sortOrder : 'asc'}
                    onClick={() => handleSortRequest('assembly')}
                  >
                    Assembly
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortBy === 'recurrence' ? sortOrder : false}>
                  <TableSortLabel
                    active={sortBy === 'recurrence'}
                    direction={sortBy === 'recurrence' ? sortOrder : 'asc'}
                    onClick={() => handleSortRequest('recurrence')}
                  >
                    Recurrence
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortBy === 'nextDueDate' ? sortOrder : false}>
                  <TableSortLabel
                    active={sortBy === 'nextDueDate'}
                    direction={sortBy === 'nextDueDate' ? sortOrder : 'asc'}
                    onClick={() => handleSortRequest('nextDueDate')}
                  >
                    Next Due
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ minWidth: 180, maxWidth: 280 }}>Last Performed</TableCell>
                <TableCell sx={{ minWidth: 180, maxWidth: 280 }}>Current Value</TableCell>
                <TableCell sx={{ minWidth: 200, maxWidth: 320 }}>Remaining (live)</TableCell>
                <TableCell sortDirection={sortBy === 'checks' ? sortOrder : false} align="right">
                  <TableSortLabel
                    active={sortBy === 'checks'}
                    direction={sortBy === 'checks' ? sortOrder : 'asc'}
                    onClick={() => handleSortRequest('checks')}
                  >
                    Checks
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortBy === 'workOrders' ? sortOrder : false} align="right">
                  <TableSortLabel
                    active={sortBy === 'workOrders'}
                    direction={sortBy === 'workOrders' ? sortOrder : 'asc'}
                    onClick={() => handleSortRequest('workOrders')}
                  >
                    Work Orders
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedBulletins.map((b) => (
                <TableRow
                  key={b._id}
                  hover
                  onClick={() => handleRowClick(b)}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: 'action.hover' }
                  }}
                >
                  <TableCell sx={{ fontFamily: 'monospace' }}>{getAdsbBulletinNumber(b)}</TableCell>
                  <TableCell>{b.title || '—'}</TableCell>
                  <TableCell>
                    <Chip size="small" label={classificationLabel(b.classification)} />
                  </TableCell>
                  <TableCell>{priorityLabel(b.priority)}</TableCell>
                  <TableCell>{sourceLabel(b.source)}{b.source === 'OTHER' && b.sourceCustom ? ` (${b.sourceCustom})` : ''}</TableCell>
                  <TableCell>{aircraftSummary(b)}</TableCell>
                  <TableCell sx={{ minWidth: 160, maxWidth: 280, verticalAlign: 'top' }}>
                    {(() => {
                      const text = assemblySummary(b);
                      if (text === '—') return '—';
                      return (
                        <Tooltip title={text}>
                          <Typography
                            variant="body2"
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical'
                            }}
                          >
                            {text}
                          </Typography>
                        </Tooltip>
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    {b.recurrenceType === 'RECURRING'
                      ? (b.matchType === 'BETWEEN' && b.recurrenceValue2 != null
                          ? `Every ${b.recurrenceValue ?? ''}–${b.recurrenceValue2} ${b.recurrenceBasedOn || ''}`
                          : `Every ${b.recurrenceValue ?? ''} ${b.recurrenceBasedOn || ''}`)
                      : 'One-time'}
                  </TableCell>
                  <TableCell sx={{ minWidth: 120, maxWidth: 220, verticalAlign: 'top' }}>
                    {(() => {
                      const lines = nextDueDisplayLines(b);
                      if (!lines.length) return '—';
                      const full = lines.join('\n');
                      const shown = lines.slice(0, 3);
                      const more = lines.length > 3 ? ` (+${lines.length - 3} more)` : '';
                      return (
                        <Tooltip title={<Box component="span" sx={{ whiteSpace: 'pre-line' }}>{full}</Box>}>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-line', cursor: 'default', lineHeight: 1.6 }}>
                            {shown.join('\n')}
                            {more}
                          </Typography>
                        </Tooltip>
                      );
                    })()}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 280, verticalAlign: 'top' }}>
                    {(() => {
                      const lines = formatBulletinLastPerformedLines(b);
                      if (lines.length === 0) return '—';
                      const full = lines.join('\n');
                      const shown = lines.slice(0, 3);
                      const more = lines.length > 3 ? ` (+${lines.length - 3} more)` : '';
                      return (
                        <Tooltip title={<Box component="span" sx={{ whiteSpace: 'pre-line' }}>{full}</Box>}>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-line', cursor: 'default' }}>
                            {shown.join('\n')}
                            {more}
                          </Typography>
                        </Tooltip>
                      );
                    })()}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 280, verticalAlign: 'top' }}>
                    {(() => {
                      const lines = formatBulletinCurrentValueLines(b);
                      if (lines.length === 0) return '—';
                      const full = lines.join('\n');
                      const shown = lines.slice(0, 3);
                      const more = lines.length > 3 ? ` (+${lines.length - 3} more)` : '';
                      return (
                        <Tooltip title={<Box component="span" sx={{ whiteSpace: 'pre-line' }}>{full}</Box>}>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-line', cursor: 'default' }}>
                            {shown.join('\n')}
                            {more}
                          </Typography>
                        </Tooltip>
                      );
                    })()}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 320, verticalAlign: 'top' }}>
                    {(() => {
                      const lines = formatBulletinRemainingLines(b);
                      if (lines.length === 0) return '—';
                      const full = lines.join('\n');
                      const shown = lines.slice(0, 3);
                      const more = lines.length > 3 ? ` (+${lines.length - 3} more)` : '';
                      return (
                        <Tooltip title={<Box component="span" sx={{ whiteSpace: 'pre-line' }}>{full}</Box>}>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-line', cursor: 'default' }}>
                            {shown.join('\n')}
                            {more}
                          </Typography>
                        </Tooltip>
                      );
                    })()}
                  </TableCell>
                  <TableCell align="right">{b.adsbChecks?.length ?? 0}</TableCell>
                  <TableCell align="right">{b.workOrders?.length ?? 0}</TableCell>
                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    <BulletinActionButtons
                      bulletin={b}
                      onView={handleView}
                      onEdit={handleEdit}
                      onCreateIntimation={handleCreateIntimation}
                      onDelete={handleDeleteClick}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {sortedBulletins.length > 0 && (
            <TablePagination
              component="div"
              count={sortedBulletins.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage="Rows per page:"
              labelDisplayedRows={({ from, to, count }) => `${from}–${to} of ${count !== -1 ? count : `more than ${to}`}`}
            />
          )}
        </TableContainer>
      )}

      <CreateEditADSBulletinDialog
        open={openDialog}
        onClose={handleCloseDialog}
        onSuccess={handleSuccess}
        onEvidenceUpload={handleEvidenceUpload}
        bulletin={selectedBulletin}
        aircraft={aircraft}
        readOnly={viewMode}
        onSwitchToEdit={handleSwitchToEdit}
      />

      <CreateIntimationFromADSBulletinDialog
        open={createIntimationOpen}
        onClose={handleCloseCreateIntimation}
        onSuccess={handleSuccessCreateIntimation}
        bulletin={bulletinForIntimation}
      />

      <Dialog open={deleteConfirmOpen} onClose={handleCloseDelete}>
        <DialogTitle>Delete AD/SB Bulletin</DialogTitle>
        <DialogContent>
          {bulletinToDelete && (
            <Typography>
              Are you sure you want to delete &quot;{bulletinToDelete.title}&quot;? This cannot be undone.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDelete}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ADSBServiceBulletins;
